import { styled } from '@mui/material/styles';
import Button from 'src/presentations/components/atom/button';
import Modal from 'src/presentations/components/molecules/modal';
import { useCallback, useState } from 'react';
import { openToast } from 'src/use-cases/toast/useToast';
import { compressToURI } from 'lz-ts';
import { stringify } from 'superjson';
import { useSearchParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useSaveReport, useUpdateReport } from '../useAnalytics';
import { UpdateChartReportOption } from './UpdateChartReportOption';
import { CreateChartReportOption } from './CreateChartReportOption';
import { useAnalyticsData } from '../context/AnalyticsDataContext';

interface IModalProps {
  open: boolean;
  title: string;
  buttonLabel?: string;
  onClose: () => void;
  setDefaultFilters: any;
  selectedChartView;
}

const StyledModal = styled(Modal)({
  zIndex: 9999,

  '& .MuiDialog-paper': {
    maxWidth: '540px',
    borderRadius: '16px',
  },
});

enum SaveType {
  UPDATE,
  CREATE,
}

export const SaveReportModal: React.FC<IModalProps> = (props) => {
  const { open, title, onClose, setDefaultFilters, selectedChartView } = props;
  const [saveType, setSaveType] = useState<SaveType>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: createReport } = useSaveReport();
  const { mutateAsync: updateReport } = useUpdateReport();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { watch } = useFormContext();
  const { filters: tableFilters } = useAnalyticsData();

  console.log('[SaveReportModal] Modal open:', open, 'Table filters:', tableFilters);

  const { name, description, ...formValues } = watch();

  const onCloseModal = () => {
    setSaveType(null);
    onClose();
  };

  const saveReport = useCallback(
    async (selectedChartView, formValues) => {
      // Validation
      if (!name?.trim()) {
        openToast({
          toastType: 'error',
          title: 'Validation Error',
          description: 'Report name is required',
        });
        return;
      }

      if (saveType === null) {
        openToast({
          toastType: 'error',
          title: 'Validation Error',
          description: 'Please select save type (Create or Update)',
        });
        return;
      }

      const currentReport = {
        name,
        description,
        filters: {
          selectedChartView,
          formValues,
        },
        tableFilters,
      };

      console.log('[SaveReportModal] Saving report with table filters:', tableFilters);
      console.log('[SaveReportModal] Current report object:', currentReport);

      setIsSaving(true);

      try {
        if (saveType === SaveType.CREATE) {
          const response = await createReport({
            name: name.trim(),
            description: description || '',
            type: 'REPORT_ANALYTIC',
            hashedFilters: compressToURI(stringify(currentReport)),
          });
          console.log('[SaveReportModal] Create response:', response);
        } else {
          if (!id) {
            openToast({
              toastType: 'error',
              title: 'Update Error',
              description: 'No report ID found for update',
            });
            return;
          }
          const response = await updateReport({
            id,
            name: name.trim(),
            description: description || '',
            type: 'REPORT_ANALYTIC',
            hashedFilters: compressToURI(stringify(currentReport)),
          });
          console.log('[SaveReportModal] Update response:', response);
        }

        // Only close modal and show success if we reach this point
        onClose();
        openToast({
          toastType: 'success',
          title: `${name} successfully ${saveType === SaveType.CREATE ? 'created' : 'updated'}`,
        });
        setDefaultFilters(currentReport);
      } catch (error) {
        console.error('[SaveReportModal] Error saving report:', error);

        // Extract meaningful error message
        let errorMessage = 'Failed to save report';
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as any;
          if (axiosError.response?.data?.message) {
            errorMessage = axiosError.response.data.message;
          }
        } else if (error && typeof error === 'object' && 'message' in error) {
          const genericError = error as any;
          if (genericError.message) {
            errorMessage = genericError.message;
          }
        }

        openToast({
          toastType: 'error',
          title: 'Save Failed',
          description: errorMessage,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [createReport, description, id, name, onClose, saveType, setDefaultFilters, tableFilters, updateReport]
  );

  return (
    <StyledModal open={open} title={title} fullWidth onClose={onCloseModal}>
      <div className='relative '>
        <div className='flex flex-col gap-[24px]'>
          <UpdateChartReportOption disabled={!id} onUpdate={() => setSaveType(SaveType.UPDATE)} isChosen={saveType === SaveType.UPDATE} />
          <CreateChartReportOption onCreate={() => setSaveType(SaveType.CREATE)} isChosen={saveType === SaveType.CREATE} />
        </div>

        <div className='mt-48 flex gap-[12px]'>
          <Button className='basis-1/2' variant='primary_outline' onClick={onCloseModal}>
            Cancel
          </Button>
          <Button
            className='basis-1/2'
            variant='primary'
            onClick={async () => await saveReport(selectedChartView, formValues)}
            disabled={saveType === null || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </StyledModal>
  );
};
